import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import MainApi from "../../../../../../services/mainApi.service";
import { useCallback, useMemo, useState } from "react";
import { ITicketAutomationReceivedConfFile } from "../../../../../../models/ticket-automation-received-conf-files";
import AutomationReceivedConfFile from "./AutomationReceivedConfFilesForm/AutomationReceivedConfFile";

const TICKET_AUTOMATION_RECEIVED_SLOT_ROUTE =
  process.env.REACT_APP_AUTOMATIONS +
  "/api/v1/TicketAutomationReceivedConfFiles/";
const ticketAutomationReceivedConfFileService =
  new MainApi<ITicketAutomationReceivedConfFile>(
    TICKET_AUTOMATION_RECEIVED_SLOT_ROUTE,
  );

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedConfFile.";

const AutomationReceivedConfFilePage = ({
  automationCode,
}: {
  automationCode?: string;
}) => {
  const { t } = useTranslation();

  const { id: ticketAutomationReceivedRuleId } = useParams();

  const [timeRuleForm, setTimeRuleForm] = useState(false);

  const [selectedId, setSelectedId] = useState<string>();

  const columns: GridColDef<ITicketAutomationReceivedConfFile>[] = [
    {
      field: "destinationCalendarFile",
      headerName:
        t(translationPath + "destination_calendar_file") ||
        "Destination Calendar File",
      renderCell: (params) =>
        params.row.destinationCalendarFile
          ? params.row.destinationCalendarFile.name
          : "-",
    },
    {
      field: "displayOrder",
      headerName: t(translationPath + "displayOrder") || "Display Order",
    },
    {
      field: "waitLimitTime",
      headerName: t(translationPath + "wait_limit_time") || "Wait Limit Time",
    },
    {
      field: "ticketAutomationReceivedConfFileCalendars",
      headerName:
        t(translationPath + "ticketAutomationReceivedConfFileCalendars") ||
        "Calendars",
      renderCell: ({ row: { ticketAutomationReceivedConfFileCalendars } }) => {
        if (!ticketAutomationReceivedConfFileCalendars) return "-";
        const calendars = ticketAutomationReceivedConfFileCalendars
          .map((item) => item.externalName)
          .join("; ");
        return <OverflowTooltip text={calendars} tooltip={calendars} />;
      },
    },
    {
      field: "ticketAutomationReceivedConfFileCalePreferences",
      headerName:
        t(
          translationPath + "ticketAutomationReceivedConfFileCalePreferences",
        ) || "Preferences",
      renderCell: ({
        row: { ticketAutomationReceivedConfFileCalePreferences },
      }) => {
        if (!ticketAutomationReceivedConfFileCalePreferences) return "-";
        const preferences = ticketAutomationReceivedConfFileCalePreferences
          .map((item) => item.externalName)
          .join("; ");
        return <OverflowTooltip text={preferences} tooltip={preferences} />;
      },
    },
    {
      field: "ticketAutomationReceivedConfFileCaleProcedures",
      headerName:
        t(translationPath + "ticketAutomationReceivedConfFileCaleProcedures") ||
        "Procedures",
      renderCell: ({
        row: { ticketAutomationReceivedConfFileCaleProcedures },
      }) => {
        if (!ticketAutomationReceivedConfFileCaleProcedures) return "-";
        const procedures = ticketAutomationReceivedConfFileCaleProcedures
          .map((item) => item.externalName)
          .join("; ");
        return <OverflowTooltip text={procedures} tooltip={procedures} />;
      },
    },
    {
      field: "ticketAutomationReceivedConfFileCaleInsurances",
      headerName:
        t(translationPath + "ticketAutomationReceivedConfFileCaleInsurances") ||
        "Insurances",
      renderCell: ({
        row: { ticketAutomationReceivedConfFileCaleInsurances },
      }) => {
        if (!ticketAutomationReceivedConfFileCaleInsurances) return "-";
        const insurances = ticketAutomationReceivedConfFileCaleInsurances
          .map((item) => item.externalName)
          .join("; ");
        return <OverflowTooltip text={insurances} tooltip={insurances} />;
      },
    },
    {
      field: "ticketAutomationReceivedConfFileCaleInsuranceTypes",
      headerName:
        t(
          translationPath +
            "ticketAutomationReceivedConfFileCaleInsuranceTypes",
        ) || "Insurance Types",
      renderCell: ({
        row: { ticketAutomationReceivedConfFileCaleInsuranceTypes },
      }) => {
        if (!ticketAutomationReceivedConfFileCaleInsuranceTypes) return "-";
        const insuranceTypes =
          ticketAutomationReceivedConfFileCaleInsuranceTypes
            .map((item) => item.externalName)
            .join("; ");
        return (
          <OverflowTooltip text={insuranceTypes} tooltip={insuranceTypes} />
        );
      },
    },
    {
      field: "message",
      headerName: t(translationPath + "message") || "Message",
    },
  ];

  const handleAdd = () => {
    setTimeRuleForm(true);
  };

  const handleRowClick = (
    params: GridRowParams<ITicketAutomationReceivedConfFile>,
  ) => {
    if (!params.row) return;
    setTimeRuleForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = useCallback(() => {
    setSelectedId(undefined);
    setTimeRuleForm(false);
  }, []);
  const props = useMemo(() => {
    return {
      TicketAutomationReceivedRuleId: ticketAutomationReceivedRuleId,
    };
  }, [ticketAutomationReceivedRuleId]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {timeRuleForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <AutomationReceivedConfFile
            id={selectedId}
            automationCode={automationCode}
            ticketAutomationReceivedRuleId={ticketAutomationReceivedRuleId}
            onBackButton={handleBack}
          />
        </Box>
      )}
      {!timeRuleForm && (
        <Box height={600}>
          <Table<ITicketAutomationReceivedConfFile>
            showDefaultMacroFilters={false}
            service={ticketAutomationReceivedConfFileService}
            columns={columns}
            title={""}
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputAutoFocus={false}
            defaultPageSize={20}
            serviceProps={props}
            pageSizeOptions={[5, 10, 20]}
            searchInputPlaceHolder={""}
            addButtonLabel={t(translationPath + "add_file") || "Add File"}
            confirmOptions={{
              title: t(translationPath + "confirm.title"),
              description: t(translationPath + "confirm.description"),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default AutomationReceivedConfFilePage;
